import { useCallback, useEffect, useState } from 'react';
import { api } from '../api';
import { useWeekBoundary } from '../useWeekBoundary';
import {
  Card,
  Chip,
  Spinner,
  addDays,
  dayName,
  shortDate,
  startOfWeek,
  todayIso,
  useToast,
} from '../components/ui';
import type { Language, ShoppingList } from '../types';

const T = {
  en: {
    title: 'Shopping',
    create: 'Create shopping lists',
    recreate: 'Rebuild lists',
    email: 'Email list',
    toWhatsapp: 'Send to WhatsApp',
    sentToWhatsapp: 'Sent to the family WhatsApp group',
    print: 'Print list',
    done: 'Mark shopping done',
    undone: 'Not shopped yet',
    completed: 'Shopped',
    pastHint: 'Taken as done. If this shop did not happen, skip it and its shopping moves to the next one.',
    nothing: 'No shopping lists yet. Plan the week first, then create the lists.',
    prev: 'Previous week',
    next: 'Next week',
    thisWeek: 'This week',
    current: 'current week',
    showPast: (n: number) => (n === 1 ? 'Show 1 earlier trip' : `Show ${n} earlier trips`),
    hidePast: 'Hide earlier trips',
    earlier: 'earlier',
    schedule: 'Shops coming up',
    today: 'today',
    tomorrow: 'tomorrow',
    inDays: (n: number) => `in ${n} days`,
    noneAhead: 'No shops left this week.',
    addShop: 'Add a shop',
    addHint: 'Just for this week — your usual shopping days are unchanged.',
    addTripButton: 'Add',
    tripAdded: 'Added. The plan can use it from that day on.',
    moved: 'Moved',
    dropped: 'Removed from this week',
    move: 'Move',
    dropTrip: 'Remove',
    shops: {
      supermarket: 'Supermarket',
      butcher: 'Butcher',
      fishmonger: 'Fishmonger',
      market: 'Market',
      boulangerie: 'Boulangerie',
    } as Record<string, string>,
    empty: 'Nothing to buy on this trip.',
    addItem: 'Add something',
    addPlaceholder: 'Milk, kitchen roll…',
    added: 'Added to the list',
    remove: 'Remove',
    removed: 'Removed',
    noMatch: 'Not in the ingredient list — try another word.',
    off: 'Not using this trip',
    turnOn: 'Use this trip',
    turnOff: 'Skip this trip',
    items: (n: number) => `${n} item${n === 1 ? '' : 's'}`,
    sent: (to: string[]) => `Sent to ${to.join(', ')}`,
    notSent: 'No mail server configured — the list was logged on the server instead',
    covers: 'Covers',
  },
  fr: {
    title: 'Courses',
    create: 'Créer les listes',
    recreate: 'Regénérer les listes',
    email: 'Envoyer par e-mail',
    toWhatsapp: 'Envoyer sur WhatsApp',
    sentToWhatsapp: 'Envoyée dans le groupe WhatsApp de la famille',
    print: 'Imprimer',
    done: 'Courses faites',
    undone: 'Pas encore fait',
    completed: 'Fait',
    pastHint: 'Considérées comme faites. Si ces courses n’ont pas eu lieu, ignorez cette sortie : ce qu’elle couvrait passera sur la suivante.',
    nothing: "Pas encore de liste. Planifiez d'abord la semaine.",
    prev: 'Semaine précédente',
    next: 'Semaine suivante',
    thisWeek: 'Cette semaine',
    current: 'semaine en cours',
    showPast: (n: number) =>
      n === 1 ? 'Afficher 1 sortie passée' : `Afficher ${n} sorties passées`,
    hidePast: 'Masquer les sorties passées',
    earlier: 'passée',
    schedule: 'Prochaines courses',
    today: 'aujourd’hui',
    tomorrow: 'demain',
    inDays: (n: number) => `dans ${n} jours`,
    noneAhead: 'Plus de courses cette semaine.',
    addShop: 'Ajouter une sortie',
    addHint: 'Pour cette semaine seulement — vos jours habituels ne changent pas.',
    addTripButton: 'Ajouter',
    tripAdded: 'Ajouté. Le plan peut en tenir compte à partir de ce jour.',
    moved: 'Déplacé',
    dropped: 'Retiré de cette semaine',
    move: 'Déplacer',
    dropTrip: 'Retirer',
    shops: {
      supermarket: 'Supermarché',
      butcher: 'Boucherie',
      fishmonger: 'Poissonnerie',
      market: 'Marché',
      boulangerie: 'Boulangerie',
    } as Record<string, string>,
    empty: 'Rien à acheter pour cette sortie.',
    addItem: 'Ajouter quelque chose',
    addPlaceholder: 'Lait, essuie-tout…',
    added: 'Ajouté à la liste',
    remove: 'Retirer',
    removed: 'Retiré',
    noMatch: 'Absent de la liste d’ingrédients — essayez un autre mot.',
    off: 'Sortie non utilisée',
    turnOn: 'Utiliser cette sortie',
    turnOff: 'Ignorer cette sortie',
    items: (n: number) => `${n} article${n === 1 ? '' : 's'}`,
    sent: (to: string[]) => `Envoyé à ${to.join(', ')}`,
    notSent: "Pas de serveur mail configuré — la liste a été enregistrée côté serveur",
    covers: 'Couvre',
  },
} as const;

export function ShoppingPage({ language }: { language: Language }) {
  const t = T[language];
  const toast = useToast();
  const [lists, setLists] = useState<ShoppingList[] | null>(null);
  const [busy, setBusy] = useState(false);
  // The shop you are about to do opens the week it feeds — the week now starts
  // on the main shop, so the two are the same thing rather than a Saturday
  // trip filed under the Monday it served.
  const todayDate = todayIso();
  const boundary = useWeekBoundary(todayDate);
  const [weekStart, setWeekStart] = useState<string | null>(null);

  useEffect(() => {
    if (!boundary) return;
    setWeekStart((current) => current ?? boundary.planningWeek);
  }, [boundary]);
  const [showPast, setShowPast] = useState(false);

  const load = useCallback(async () => {
    if (!weekStart) return;
    const result = await api.shopping(weekStart, language);
    setLists(result.lists);
  }, [weekStart, language]);

  useEffect(() => {
    void load();
  }, [load]);

  const guard = async (action: () => Promise<unknown>) => {
    setBusy(true);
    try {
      await action();
      await load();
    } catch (error) {
      toast(error instanceof Error ? error.message : 'Something went wrong');
    } finally {
      setBusy(false);
    }
  };

  const toggleItem = async (list: ShoppingList, itemId: string, checked: boolean) => {
    // Optimistic: ticking things off in a supermarket must feel instant.
    setLists((current) =>
      current?.map((l) =>
        l.tripId !== list.tripId
          ? l
          : {
              ...l,
              categories: l.categories.map((c) => ({
                ...c,
                items: c.items.map((i) => (i.id === itemId ? { ...i, checked } : i)),
              })),
            },
      ) ?? null,
    );
    try {
      await api.checkItem(itemId, checked);
    } catch {
      void load();
    }
  };

  if (!lists || !weekStart) {
    return (
      <div style={{ display: 'grid', placeItems: 'center', minHeight: '50vh' }}>
        <Spinner />
      </div>
    );
  }

  const active = lists.filter((list) => list.enabled);
  const hasItems = active.some((list) => list.itemCount > 0);

  // A shop that has already been and gone is a record, not a job. It stays
  // available — you may want to see what came home — but it does not sit at
  // the top of the page pretending to be outstanding.
  const isCurrentWeek = weekStart === boundary?.thisWeek;
  const past = lists.filter((list) => list.date < todayDate);
  const upcoming = lists.filter((list) => list.date >= todayDate);
  const visible = showPast ? [...past, ...upcoming] : upcoming;

  return (
    <div className="stack">
      <div className="row" style={{ gap: 10, flexWrap: 'wrap' }}>
        <h1 style={{ marginRight: 'auto' }}>{t.title}</h1>

        <div className="row week-nav" style={{ gap: 6 }}>
          <button
            className="btn small"
            aria-label={t.prev}
            onClick={() => setWeekStart((w) => (w ? addDays(w, -7) : w))}
          >
            ‹
          </button>
          <span className="week-range">
            {dayName(weekStart, language).slice(0, 3)} {shortDate(weekStart, language)} –{' '}
            {shortDate(addDays(weekStart, 6), language)}
            {isCurrentWeek && <span className="dim"> · {t.current}</span>}
          </span>
          <button
            className="btn small"
            aria-label={t.next}
            onClick={() => setWeekStart((w) => (w ? addDays(w, 7) : w))}
          >
            ›
          </button>
          {!isCurrentWeek && (
            <button className="btn small" onClick={() => boundary && setWeekStart(boundary.thisWeek)}>
              {t.thisWeek}
            </button>
          )}
        </div>

        <button className="btn primary" disabled={busy} onClick={() => guard(() => api.generateShopping(weekStart, language))}>
          {hasItems ? t.recreate : t.create}
        </button>

        {/* The group is where the shopping actually gets done, so this is
            worth a button of its own and not only the finalise-time setting:
            lists get rebuilt and edited after the week is finalised. */}
        {hasItems && (
          <button
            className="btn"
            disabled={busy}
            onClick={() =>
              void guard(async () => {
                await api.sendShoppingToWhatsApp(weekStart, language);
                toast(t.sentToWhatsapp);
              })
            }
          >
            {t.toWhatsapp}
          </button>
        )}
      </div>

      {/* The schedule first: when the next shops are, before any list. This
          is the question you open the page with. */}
      <Card title={t.schedule}>
        {upcoming.filter((l) => l.enabled).length === 0 ? (
          <p className="dim">{t.noneAhead}</p>
        ) : (
          <div className="stack" style={{ gap: 8 }}>
            {upcoming
              .filter((list) => list.enabled)
              .map((list) => {
                const days = Math.round(
                  (Date.parse(`${list.date}T00:00:00Z`) - Date.parse(`${todayDate}T00:00:00Z`)) /
                    86_400_000,
                );
                const when = days === 0 ? t.today : days === 1 ? t.tomorrow : t.inDays(days);
                return (
                  <div className="row shop-line" key={list.tripId} style={{ gap: 10 }}>
                    <span style={{ fontWeight: 600, minWidth: 108 }}>{list.shopLabel}</span>
                    <span className="dim" style={{ flex: 1, minWidth: 130 }}>
                      {dayName(list.date, language)} {shortDate(list.date, language)} · {when}
                    </span>
                    {list.completed ? (
                      <Chip kind="accent">{t.completed}</Chip>
                    ) : (
                      <Chip>{t.items(list.itemCount)}</Chip>
                    )}
                    {!list.completed && (
                      <>
                        <input
                          type="date"
                          aria-label={`${t.move} ${list.shopLabel}`}
                          value={list.date}
                          disabled={busy}
                          onChange={(event) =>
                            event.target.value &&
                            void guard(async () => {
                              setLists(await api.moveTrip(list.tripId, event.target.value));
                              toast(t.moved);
                            })
                          }
                        />
                        <button
                          className="btn icon small"
                          title={t.dropTrip}
                          aria-label={`${t.dropTrip} ${list.shopLabel}`}
                          disabled={busy}
                          onClick={() =>
                            void guard(async () => {
                              setLists(await api.removeTrip(list.tripId));
                              toast(t.dropped);
                            })
                          }
                        >
                          ×
                        </button>
                      </>
                    )}
                  </div>
                );
              })}
          </div>
        )}

        <form
          className="row add-shop"
          style={{ gap: 8, marginTop: 14, flexWrap: 'wrap' }}
          onSubmit={(event) => {
            event.preventDefault();
            const form = event.currentTarget;
            const data = new FormData(form);
            const tripDate = String(data.get('tripDate') ?? '');
            const shopType = String(data.get('shopType') ?? 'supermarket');
            if (!tripDate) return;
            void guard(async () => {
              setLists(await api.addTrip(weekStart, tripDate, shopType));
              toast(t.tripAdded);
              form.reset();
            });
          }}
        >
          <select name="shopType" aria-label={t.addShop} style={{ minWidth: 150 }}>
            {Object.entries(t.shops).map(([value, label]) => (
              <option key={value} value={value}>
                {label}
              </option>
            ))}
          </select>
          <input
            name="tripDate"
            type="date"
            required
            aria-label={t.addShop}
            min={weekStart}
            max={addDays(weekStart, 6)}
          />
          <button className="btn" type="submit" disabled={busy}>
            + {t.addTripButton}
          </button>
        </form>
        <p className="dim" style={{ fontSize: 13, marginTop: 8 }}>
          {t.addHint}
        </p>
      </Card>

      {past.length > 0 && (
        <div className="row">
          <button className="btn small" onClick={() => setShowPast((v) => !v)}>
            {showPast ? t.hidePast : t.showPast(past.length)}
          </button>
        </div>
      )}

      {!hasItems && <p className="banner">{t.nothing}</p>}

      {visible.map((list) => (
        <Card
          key={list.tripId}
          className={list.date < todayDate ? 'trip-past' : undefined}
          title={
            <div>
              <h2>
                {dayName(list.date, language)} — {list.shopLabel}
              </h2>
              <div className="row" style={{ gap: 6, marginTop: 4 }}>
                <span className="dim">{shortDate(list.date, language)}</span>
                {list.enabled && <Chip>{t.items(list.itemCount)}</Chip>}
                {list.required && <Chip kind="accent">Main shop</Chip>}
                {list.completed && <Chip kind="accent">{t.completed}</Chip>}
                {list.date < todayDate && !list.completed && <Chip>{t.earlier}</Chip>}
                {!list.enabled && <Chip>{t.off}</Chip>}
              </div>
            </div>
          }
          actions={
            <div className="row" style={{ gap: 8 }}>
              <button className="btn small" disabled={busy} onClick={() => guard(() => api.enableTrip(list.tripId, !list.enabled))}>
                {list.enabled ? t.turnOff : t.turnOn}
              </button>
              {list.enabled && list.itemCount > 0 && (
                <>
                  <a
                    className="btn small"
                    href={api.printUrl(weekStart, [list.tripId], language)}
                    target="_blank"
                    rel="noreferrer"
                  >
                    {t.print}
                  </a>
                  <button
                    className="btn small"
                    disabled={busy}
                    onClick={() =>
                      guard(async () => {
                        const result = await api.emailShopping(weekStart, [list.tripId], language);
                        toast(result.delivered ? t.sent(result.recipients) : t.notSent);
                      })
                    }
                  >
                    {t.email}
                  </button>
                </>
              )}
            </div>
          }
        >
          {!list.enabled ? (
            <p className="dim">{t.off}</p>
          ) : list.itemCount === 0 ? (
            <p className="dim">{t.empty}</p>
          ) : (
            <>
              {list.date < todayDate && !list.completed && <p className="dim">{t.pastHint}</p>}
              {list.categories.map((category) => (
                <div key={category.category}>
                  <h3 className="cat-head">{category.label}</h3>
                  {category.items.map((item) => (
                    <div
                      key={item.id}
                      className={`shop-item${item.checked ? ' checked' : ''}`}
                      role="checkbox"
                      aria-checked={item.checked}
                      tabIndex={0}
                      onClick={() => void toggleItem(list, item.id, !item.checked)}
                      onKeyDown={(event) => {
                        if (event.key === ' ' || event.key === 'Enter') {
                          event.preventDefault();
                          void toggleItem(list, item.id, !item.checked);
                        }
                      }}
                    >
                      <span className="tickbox" aria-hidden="true">
                        ✓
                      </span>
                      <span className="name">
                        {item.name}
                        {item.manual && <span className="dim"> · added</span>}
                      </span>
                      <span className={item.purchaseQuantity > 0 ? 'qty' : 'qty in-cupboard'}>
                        {item.purchaseLabel}
                      </span>
                      {/* Stops the click from also ticking the line off. */}
                      <button
                        className="btn icon small"
                        title={t.remove}
                        aria-label={`${t.remove} ${item.name}`}
                        disabled={busy}
                        onClick={(event) => {
                          event.stopPropagation();
                          void guard(async () => {
                            await api.removeItem(item.id);
                            toast(t.removed);
                          });
                        }}
                      >
                        ×
                      </button>
                    </div>
                  ))}
                </div>
              ))}

              <AddItemRow
                tripId={list.tripId}
                language={language}
                disabled={busy}
                onAdded={() => void guard(async () => toast(t.added))}
              />

              <div className="row" style={{ marginTop: 20 }}>
                <button
                  className={`btn${list.completed ? ' on' : ' primary'}`}
                  disabled={busy}
                  onClick={() => guard(() => api.completeTrip(list.tripId, !list.completed))}
                >
                  {list.completed ? t.undone : t.done}
                </button>
              </div>
            </>
          )}
        </Card>
      ))}
    </div>
  );
}

/**
 * Adding something the plan did not ask for — "we're out of kitchen roll".
 *
 * It resolves against the ingredient library rather than accepting free text,
 * because a line the system understands lands in the right aisle and is
 * recognised later when the shop is marked done; a free-text one is just a
 * note. So the box searches, and you pick.
 */
function AddItemRow({
  tripId,
  language,
  disabled,
  onAdded,
}: {
  tripId: string;
  language: Language;
  disabled: boolean;
  onAdded: () => void;
}) {
  const t = T[language];
  const [query, setQuery] = useState('');
  const [options, setOptions] = useState<Array<{ id: string; name: string }>>([]);
  const [all, setAll] = useState<Array<{ id: string; name: string }> | null>(null);

  useEffect(() => {
    if (!query.trim()) return setOptions([]);
    const run = async () => {
      const library = all ?? (await api.ingredients());
      if (!all) setAll(library);
      const q = query.trim().toLowerCase();
      setOptions(library.filter((i) => i.name.toLowerCase().includes(q)).slice(0, 6));
    };
    void run();
  }, [query, all]);

  return (
    <div className="add-item">
      <input
        type="search"
        value={query}
        placeholder={t.addPlaceholder}
        aria-label={t.addItem}
        disabled={disabled}
        onChange={(event) => setQuery(event.target.value)}
      />
      {options.length > 0 && (
        <div className="add-options">
          {options.map((option) => (
            <button
              key={option.id}
              className="btn small"
              disabled={disabled}
              onClick={async () => {
                await api.addItem(tripId, option.id, 1);
                setQuery('');
                setOptions([]);
                onAdded();
              }}
            >
              + {option.name}
            </button>
          ))}
        </div>
      )}
      {query.trim() && options.length === 0 && <p className="dim" style={{ fontSize: 13 }}>{t.noMatch}</p>}
    </div>
  );
}
